import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { readActingUserId } from '../../auth/actingUser'
import { PageHeaderActionsProvider } from '../../components/ui/pageHeaderActions'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import MobileDrawer from './MobileDrawer'
import { useSidebarCollapsed } from './useSidebarCollapsed'
import ErrorBoundary from '../../components/ui/ErrorBoundary'
import { BreadcrumbProvider } from '../nav/breadcrumb'
import ProxyBanner from '../../features/proxy/ProxyBanner'
import ProxySwitchingNotice from '../../features/proxy/ProxySwitchingNotice'
import { useProxyInvalidRecovery } from '../../features/proxy/useProxyInvalidRecovery'
import {
  completePersonaSwitchNavigation,
  usePersonaSwitching,
} from '../../features/proxy/personaSwitchState'
import './AppLayout.css'

/** Uygulama kabuğu: kalıcı sidebar + üst bar + içerik (Outlet). */
export default function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { collapsed, toggle } = useSidebarCollapsed()
  const { pathname, key: locationKey } = useLocation()
  // Çerez React state'i değil; `navigate` sonrası bu bileşen zaten yeniden render olduğu için
  // değer o commit'te okunur. Kendi personasında sabit bir etiket kullanılır ki anahtar hiç
  // undefined olmasın.
  const personaKey = `persona-${readActingUserId() ?? 'self'}`
  const navigate = useNavigate()
  // Vekalet çalışırken geçersizleşirse (089) kullanıcıyı kendi personasına düşüren kurtarma kancası.
  // Kabukta bir kez bağlanır; ekran bazında bağlansaydı davranış sayfaya göre ayrışırdı.
  useProxyInvalidRecovery()
  // Persona geçişi sürerken içerik alanı hiç render edilmez (2026-10-13). Bayrak, geçişin
  // yönlendirmesi COMMIT edildiğinde — yeni konumu gören bu efektte — kapanır; neden
  // `useProxySwitch` içinde kapatılmadığı `personaSwitchState`'te.
  const switching = usePersonaSwitching()
  useEffect(() => {
    completePersonaSwitchNavigation()
  }, [locationKey])
  // Figma "yüzen tek kart" kabuğu: içerik alanı (topbar + içerik) tüm sayfalarda tek beyaz kart
  // (gri zeminde, sidebar ayrı). İçerik boşluğu app-content padding'inden gelir.
  const carded = true
  return (
    <BreadcrumbProvider>
      <div className={`app-shell${collapsed ? ' app-shell--collapsed' : ''}`}>
        <Sidebar collapsed={collapsed} onToggle={toggle} />
        <MobileDrawer visible={drawerOpen} onHide={() => setDrawerOpen(false)} />
        <div className={`app-main${carded ? ' app-main--carded' : ''}`}>
          <Topbar onMenuToggle={() => setDrawerOpen(true)} />
          {/* Vekil modu uyarı şeridi (089): Topbar ile içerik arasında, TÜM rotalarda. Rota
              değişiminden etkilenmemesi için Outlet'in dışında durur — kullanıcı hangi sayfaya
              giderse gitsin kimin adına çalıştığını görür. */}
          <ProxyBanner />
          <main className="app-content">
            {/* Rota-seviyesi çökme koruması: bir ekran çökse de kabuk (sidebar/topbar) ayakta kalır;
                rota değişince boundary otomatik sıfırlanır (resetKeys). */}
            <ErrorBoundary resetKeys={[pathname]} onReset={() => navigate('/')}>
              {/*
                `key` = seçili persona: kimlik değişince içerik ağacı SÖKÜLÜR ve yeniden kurulur.

                Neden gerekli (2026-08-26): vekalet geçişi anasayfaya yönlendiriyordu ama yönlendirme
                React'in commit'inden önce gerçekleşmediği için eski ekran o anda hâlâ ayaktaydı;
                `invalidateQueries()` onun sorgularını YENİ persona başlığıyla yeniden çekiyor, yetkisi
                olmayan uçlar 403 dönüyor ve kullanıcı anasayfaya düşerken "bu işlem için yetkiniz yok"
                toast'ları görüyordu. Sıralamayı değiştirmek çözmez: `navigate` bir state güncellemesidir,
                aynı microtask içinde commit edilmiş olması GARANTİ DEĞİLDİR.

                Buradaki anahtar, düzeltmeyi zamanlamadan **yapıya** taşır: rota değişimiyle AYNI
                commit'te eski ağaç sökülür, dolayısıyla eski ekranın sorguları gözlemcisiz kalır ve
                hiç yeniden çekilmez. Aynı sayfada kalınsa bile (ör. geçişi anasayfada yapmak) anahtar
                değiştiği için ağaç yine kurulur → veri taze gelir, eski kimliğin verisi ekranda kalmaz.

                Kabuk (sidebar/topbar/ProxyBanner) BİLİNÇLİ olarak dışarıda: persona seçim menüsü
                topbar'dadır ve tıklamanın ortasında sökülmemelidir.
              */}
              {/*
                Geçiş sürerken içerik GİZLENMEZ, SÖKÜLÜR (2026-10-13). Gizlemek (`hidden`) yetmezdi:
                bu bileşen geçiş durumuna abone olduğu için yönlendirmeden hemen önce de render olur;
                o an çerez yeni personayı gösterir ama konum hâlâ eski ekrandır — gizli de olsa eski
                rotada yeni anahtarla bir ağaç kurulur ve sorguları yeni kimlikle gider (403 → "yetkiniz
                yok" toast'ı). Bedeli: geçiş reddedilirse ekran yeniden kurulur (veri önbellekten anında
                gelir, yarım bir form girdisi kaybolur) — nadir bir durum için bilinçle kabul edildi.
              */}
              {switching ? (
                <ProxySwitchingNotice />
              ) : (
                <div key={personaKey} className="app-content-persona">
                  {/* Sayfa başlığı aksiyon slotu — PageHeader kabı bildirir, paneller portal çizer.
                      Provider içerik ağacının İÇİNDE: persona değişince o ağaç sökülüyor ve slot
                      referansı da onunla birlikte sıfırlanmalı (dışta kalsa sökülmüş bir düğümü
                      tutmaya devam ederdi). */}
                  <PageHeaderActionsProvider>
                    <Outlet />
                  </PageHeaderActionsProvider>
                </div>
              )}
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </BreadcrumbProvider>
  )
}
